import {
    ANALYTICS_ENABLED,
    ANALYTICS_ENDPOINT,
    ANALYTICS_MAX_QUEUED_MESSAGES,
    ANALYTICS_SUBMIT_DEBOUNCE_MS
} from "../Constants";
import {delayDebounced} from "./Utils";
import {makeUniqueSelector} from "./makeUniqueSelector";
import {Dispatchable} from "../../stem-core/src/base/Dispatcher";

export const AnalyticsEventType = {
    SDK_JS_LOADED: {key: "sdkJSLoaded", flush: false},
    SDK_INIT_CALLED: {key: "sdkInitCalled", flush: false},
    PUBLIC_DATA_LOADED: {key: "publicDataLoaded", flush: false},
    WALLET_JS_LOADED: {key: "walletJSLoaded", flush: false},
    SDK_DO_INIT_FINISHED: {key: "sdkDoInitFinished", flush: true},
    WALLET_DATA_LOADED: {key: "walletDataLoaded", flush: true},
    PROMPT_DONATION_POPUP: {key: "promptDonationPopup", flush: true},
    PROMPT_SUBSCRIPTION_POPUP: {key: "promptSubscriptionPopup", flush: true},
    ELEMENT_CLICK: {key: "elementClick", flush: true},
    SOCIAL_LOGIN_BUTTON_CLICKED: {key: "socialLoginButtonClicked", flush: true},
    REQUEST_EMAIL_ACCESS_CODE: {key: "requestEmailAccessCode", flush: true},
    ATTEMPT_CONFIRM_EMAIL_CODE: {key: "attemptConfirmEmailCode", flush: true},
    ATTEMPT_DONATE: {key: "attemptDonate", flush: true},
    ATTEMPT_SUBSCRIBE: {key: "attemptSubscribe", flush: true},
    DONATE_SELECTED_AMOUNT: {key: "donateSelectedAmount", flush: true},
    IFRAME_COLLAPSED: {key: "iframeCollapsed", flush: true},
    USER_LOGIN: {key: "userLogin", flush: true},
    AJAX_REQUEST_FAILED: {key: "ajaxRequestFailed", flush: false},
    AJAX_REQUEST_SUCCEEDED: {key: "ajaxRequestSucceeded", flush: false},
    TAB_FOCUS: {key: "tabFocus", flush: false},
    TAB_DEFOCUS: {key: "tabDefocus", flush: false},
    TAB_CLOSE: {key: "tabClose", flush: false},
    TAB_OPEN: {key: "tabOpen", flush: false},
    RUN_USER_JOURNEY_ACTION: {key: "runUserJourneyAction", flush: true},
    CUSTOM: {key: "custom", flush: true},  // Custom merchant event
};

export const EventSender = {
    sdk: "sdk",
    wallet: "wallet",
    paywall: "paywall",
};

let analyticsClient = null;

export class AnalyticsClient extends Dispatchable {
    constructor(eventSender, sendsWindowEvents = false) {
        super();

        if (!ANALYTICS_ENABLED || !ANALYTICS_ENDPOINT) {
            return;
        }

        analyticsClient = this;

        this.eventSender = eventSender;
        this.sendsWindowEvents = sendsWindowEvents;
        this.eventQueue = [];
        this.environment = {};
        this.identity = {};
        this.extraProperties = {};

        this.submitEnqueuedEventsDebounced = delayDebounced(
            () => this.submitEnqueuedEvents(),
            ANALYTICS_SUBMIT_DEBOUNCE_MS);
        let enqueuedWindowUnloadEvents = false;
        const sendWindowUnloadEvents = () => {
            if (this.sendsWindowEvents && !enqueuedWindowUnloadEvents) {
                dispatchAnalyticsEvent(AnalyticsEventType.TAB_CLOSE);
                enqueuedWindowUnloadEvents = true;
            }
        };

        this.attachEventListener(window, "beforeunload", () => {
            sendWindowUnloadEvents();
            this.submitEnqueuedEvents();
        });
        this.attachEventListener(window, "unload", () => {
            sendWindowUnloadEvents();
            this.submitEnqueuedEvents();
        });

        if (this.sendsWindowEvents) {
            this.attachEventListener(document, "visibilitychange", () => {
                if (document.visibilityState === "visible") {
                    dispatchAnalyticsEvent(AnalyticsEventType.TAB_FOCUS);
                } else {
                    dispatchAnalyticsEvent(AnalyticsEventType.TAB_DEFOCUS);
                }
            });
        }
    }

    submitEnqueuedEvents() {
        if (this.eventQueue.length === 0 || !!window.navigator?.doNotTrack) {
            return;
        }

        const serializePayload = (eventSubmitMethod) => JSON.stringify({
            events: this.eventQueue.map(event => Object.assign(event, {
                eventSubmitMethod: eventSubmitMethod,
                eventDequeueTime: performance.now(),
            })),
        });

        if (typeof window.navigator.sendBeacon === "function") {
            const payload = serializePayload("beacon");
            if (window.navigator.sendBeacon(ANALYTICS_ENDPOINT, payload)) {
                this.eventQueue.splice(0, this.eventQueue.length);
                return;
            }
        }

        const payload = serializePayload("xhr");
        this.eventQueue.splice(0, this.eventQueue.length);
        const xhr = new XMLHttpRequest();
        xhr.open("POST", ANALYTICS_ENDPOINT, true);
        xhr.send(payload);
    }

    enqueueEvent(eventType, properties = {}) {
        properties = {...this.extraProperties, ...properties};

        // If the event is dispatched because of a user action, attach information about the user action.
        if (window.event) {
            let target = window.event.currentTarget || window.event.target;
            // In case this is React, which attaches all listeners to the document for some reason.
            if (target === document) {
                target = window.event.target || window.event.currentTarget;
            }
            if (target && target.tagName) {
                properties.domEventType = window.event.type;
                properties.domEventTargetSelector = makeUniqueSelector(target);
                if (target.dataset && target.dataset.blinkAnid) {
                    properties.domEventTargetAnid = target.dataset.blinkAnid;
                }
            }
        }
        this.eventQueue.push({
            timestamp: +Date.now(),
            eventEnqueueTime: performance.now(),
            eventSender: this.eventSender,
            environment: this.environment,
            identity: this.identity,
            eventType: eventType.key,
            properties: properties,
        });
        if (this.eventQueue.length >= ANALYTICS_MAX_QUEUED_MESSAGES) {
            this.submitEnqueuedEvents();
        } else if (eventType.flush) {
            this.submitEnqueuedEventsDebounced();
        }
    }

    addExtraProperties(extraProperties) {
        Object.assign(this.extraProperties, extraProperties);
    }

    updateEnvironment(data) {
        Object.assign(this.environment, data);
    }

    updateIdentity(data) {
        Object.assign(this.identity, data);
    }
}

export function dispatchAnalyticsEvent(type, properties = {}) {
    if (analyticsClient) {
        try {
            analyticsClient.enqueueEvent(type, properties);
        } catch (e) {
            console.error("Blink analytics caught an error:", e);
        }
    }
}

// Useful for tests.
export function cleanupAnalyticsClient() {
    if (analyticsClient) {
        analyticsClient.cleanup();
        analyticsClient = null;
    }
}
